import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getApiBaseUrl } from "./config";
import "./App.css";

const BASE_URL = getApiBaseUrl();
const DATE_FILTERED_TYPES = new Set([
  "commission-earned",
  "draft-orders",
  "net-sales",
  "refunds",
  "total-sales",
  "todays-orders",
]);

const ORDER_TABLE_TYPES = new Set([
  "commission-earned",
  "draft-orders",
  "net-sales",
  "refunds",
  "todays-orders",
  "total-sales",
]);

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getToolbarTitle(type, now) {
  const label = now.toLocaleString("default", { month: "long", year: "numeric" });

  switch (type) {
    case "customers":
      return "Customer directory";
    case "inactive-customers":
      return "Inactive customer follow-up";
    case "draft-orders":
      return `Draft orders for ${label}`;
    case "todays-orders":
      return "Today's confirmed orders";
    default:
      return `Orders for ${label}`;
  }
}

export default function KPIDetailView() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [commissionRate, setCommissionRate] = useState(0);
  const [filtered, setFiltered] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1,
  });
  const [amountBasis, setAmountBasis] = useState("include_vat");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);

  // 🗓️ Current month bounds
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const minDate = formatLocalDate(firstDay);
  const maxDate = formatLocalDate(lastDay);

  const [startDate, setStartDate] = useState(minDate);
  const [endDate, setEndDate] = useState(maxDate);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const usesDateFilter = DATE_FILTERED_TYPES.has(type);
  const usesOrderTable = ORDER_TABLE_TYPES.has(type);
  const toolbarTitle = getToolbarTitle(type, now);

  useEffect(() => {
    const fetchData = async () => {
      const initialLoad = !hasLoadedRef.current;
      if (initialLoad) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      try {
        const query = new URLSearchParams({ type });
        if (usesDateFilter) {
          query.set("startDate", startDate);
          query.set("endDate", endDate);
        }
        query.set("page", String(page));
        query.set("pageSize", String(pageSize));

        const res = await axios.get(`${BASE_URL}/api/kpi-details?${query.toString()}`, {
          withCredentials: true,
        });

        const { data, commission, allCustomers, pagination, amountBasis } = res.data;

        setCommissionRate(commission);
        setFiltered(data || []);
        setCustomers(allCustomers || []);
        setAmountBasis(amountBasis || "include_vat");
        setPagination(
          pagination || {
            page: 1,
            pageSize: 20,
            total: (data || []).length,
            totalPages: 1,
          }
        );
        setError("");
      } catch (err) {
        console.error("❌ KPI detail fetch failed:", err.message);
        setError(err.response?.data?.error || "Could not load KPI details.");
      } finally {
        setLoading(false);
        setIsRefreshing(false);
        hasLoadedRef.current = true;
      }
    };
    fetchData();
  }, [type, startDate, endDate, page, pageSize, usesDateFilter]);

  useEffect(() => {
    setStartDate(minDate);
    setEndDate(maxDate);
    setPage(1);
    setPageSize(20);
    setSearch("");
  }, [type, minDate, maxDate]);

  const getCustomerName = (partner_id) => {
    const c = customers.find((c) => c.id === partner_id?.[0]);
    return c?.name || "Unknown";
  };

  const formatDisplayDate = (value, fallback = "—") =>
    value ? new Date(value).toLocaleDateString("en-GB") : fallback;

  const renderContactActions = (customer) => {
    const { callLink, whatsappLink } = getPhoneLinks(customer);

    if (!callLink && !whatsappLink) {
      return <span className="mobile-card-muted">No actions</span>;
    }

    return (
      <div className="mobile-card-actions">
        {callLink ? (
          <a href={callLink} className="mobile-card-action mobile-card-action-call">
            Call
          </a>
        ) : null}
        {whatsappLink ? (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mobile-card-action mobile-card-action-whatsapp"
          >
            WhatsApp
          </a>
        ) : null}
      </div>
    );
  };

  // 🏷️ Badge colors
  const getBadgeStyle = (status) => {
    if (!status) return {};
    const clean = status.replace("_", " ").toLowerCase();
    const colors = {
      invoiced: "#4caf50",
      "to invoice": "#115159",
      upselling: "#ff9800",
      "nothing to invoice": "#9e9e9e",
      no: "#9e9e9e",
    };
    return {
      backgroundColor: colors[clean] || "#9e9e9e",
      color: "white",
      borderRadius: "6px",
      padding: "3px 8px",
      textTransform: "capitalize",
      fontSize: "0.85rem",
    };
  };

  // ☎️ Phone + WhatsApp links
  const getPhoneLinks = (customer) => {
    const phone = customer.phone?.replace(/\s+/g, "") || "";
    if (!phone) return { callLink: null, whatsappLink: null };

    const digits = phone.replace(/[^0-9]/g, "");
    const agentName = localStorage.getItem("agent_name") || "your agent";
    const message = `Hi ${encodeURIComponent(
      customer.name
    )}, this is ${encodeURIComponent(agentName)} from MyStock.`;

    return {
      callLink: `tel:${digits}`,
      whatsappLink: `https://wa.me/${digits}?text=${message}`,
    };
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredData = filtered.filter((row) => {
    if (!normalizedSearch) {
      return true;
    }

    const haystack = [
      row.name,
      row.email,
      row.phone,
      row.invoice_status,
      row.partner_id?.[1],
      row.last_order_date,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(normalizedSearch);
  });

  const rowCount = pagination.total;
  const amountLabel = amountBasis === "exclude_vat" ? "Ex VAT" : "Incl. VAT";

  if (loading) {
    return (
      <div className="page-state">
        <div className="spinner" />
        <p>Loading KPI details...</p>
      </div>
    );
  }

  return (
    <div className="kpi-detail-container">
      <div className="page-topbar">
        <button className="back-link back-button" onClick={() => navigate("/")}>
          Back to Dashboard
        </button>
        <Link to="/profile" className="back-link">Profile</Link>
      </div>

      <h2 className="kpi-detail-title">
        {type === "commission-earned"
          ? `COMMISSION (${commissionRate}%)`
          : type.replace(/-/g, " ").toUpperCase()}
      </h2>

      {error && <div className="status-banner status-banner-error">{error}</div>}
      {isRefreshing && !error && (
        <div className="status-banner status-banner-info">Updating results...</div>
      )}

      <div className="detail-toolbar">
          <div className="detail-toolbar-head">
            <div className="detail-toolbar-title">{toolbarTitle}</div>
            <div className="detail-toolbar-meta">
              Showing {filteredData.length} visible rows out of {pagination.total} total results.
              {usesOrderTable ? ` Amount basis: ${amountLabel}.` : ""}
            </div>
          </div>

        <div className="detail-toolbar-controls">
          {usesDateFilter ? (
            <div className="detail-filter-group">
              <div>
                <label className="filter-label">From</label>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="filter-input"
                />
              </div>

              <div>
                <label className="filter-label">To</label>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="filter-input"
                />
              </div>

              <button
                onClick={() => {
                  setStartDate(minDate);
                  setEndDate(maxDate);
                }}
                className="toolbar-button toolbar-button-primary"
              >
                Current Month
              </button>
            </div>
          ) : null}

          <div className="detail-filter-group">
            <div>
              <label className="filter-label">Search</label>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="filter-input filter-search"
                placeholder="Search this page"
              />
            </div>

            <div>
              <label className="filter-label">Rows</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="filter-input"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* =============================== */}
      {/* 📊 TABLES */}
      {/* =============================== */}
      <div className="kpi-detail-table-wrapper">
        {!error && rowCount === 0 ? (
          <div className="empty-state">
            <h3>No records found</h3>
            <p>There is no data available for this KPI with the current filters.</p>
          </div>
        ) : null}

        {/* 🟢 ORDER TABLES */}
        {usesOrderTable && filteredData.length > 0 && (
          <>
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Untaxed</th>
                  <th>{type === "net-sales" ? `Net (${amountLabel})` : `Amount (${amountLabel})`}</th>
                  <th>Status</th>
                  {type === "commission-earned" && <th>Commission</th>}
                  {(type === "net-sales" || type === "refunds") && (
                    <th>{`Refund (${amountLabel})`}</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {filteredData.map((o) => (
                  <tr key={o.id}>
                    <td>{o.name}</td>
                    <td>{getCustomerName(o.partner_id)}</td>
                    <td>{formatDisplayDate(o.date_order)}</td>
                    <td>{`R${(o.amount_untaxed || 0).toFixed(2)}`}</td>
                    <td>{`R${(
                      type === "net-sales"
                        ? o.net_total ?? o.display_amount ?? o.amount_total ?? 0
                        : o.display_amount ?? o.amount_total ?? 0
                    ).toFixed(2)}`}</td>

                    <td>
                      <span style={getBadgeStyle(type === "draft-orders" ? o.state : o.invoice_status)}>
                        {(type === "draft-orders" ? o.state : o.invoice_status)?.replace("_", " ") || "N/A"}
                      </span>
                    </td>

                    {type === "commission-earned" && (
                      <td>{`R${(o.commission_amount || 0).toFixed(2)}`}</td>
                    )}

                    {(type === "net-sales" || type === "refunds") && (
                      <td>{`R${(o.refund_amount || 0).toFixed(2)}`}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mobile-card-list">
              {filteredData.map((o) => (
                <article className="mobile-data-card" key={o.id}>
                  <div className="mobile-card-title-row">
                    <h3 className="mobile-card-title">{o.name}</h3>
                    <span style={getBadgeStyle(type === "draft-orders" ? o.state : o.invoice_status)}>
                      {(type === "draft-orders" ? o.state : o.invoice_status)?.replace("_", " ") || "N/A"}
                    </span>
                  </div>
                  <div className="mobile-card-grid">
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Customer</span>
                      <span className="mobile-card-value">{getCustomerName(o.partner_id)}</span>
                    </div>
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Date</span>
                      <span className="mobile-card-value">{formatDisplayDate(o.date_order)}</span>
                    </div>
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Untaxed</span>
                      <span className="mobile-card-value">{`R${(o.amount_untaxed || 0).toFixed(2)}`}</span>
                    </div>
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">
                        {type === "net-sales" ? `Net (${amountLabel})` : `Amount (${amountLabel})`}
                      </span>
                      <span className="mobile-card-value">{`R${(
                        type === "net-sales"
                          ? o.net_total ?? o.display_amount ?? o.amount_total ?? 0
                          : o.display_amount ?? o.amount_total ?? 0
                      ).toFixed(2)}`}</span>
                    </div>
                    {type === "commission-earned" ? (
                      <div className="mobile-card-item">
                        <span className="mobile-card-label">Commission</span>
                        <span className="mobile-card-value">{`R${(o.commission_amount || 0).toFixed(2)}`}</span>
                      </div>
                    ) : null}
                    {type === "net-sales" || type === "refunds" ? (
                      <div className="mobile-card-item">
                        <span className="mobile-card-label">{`Refund (${amountLabel})`}</span>
                        <span className="mobile-card-value">{`R${(o.refund_amount || 0).toFixed(2)}`}</span>
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        {/* 🔵 CUSTOMERS */}
        {type === "customers" && filteredData.length > 0 && (
          <>
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.map((c) => {
                  const { callLink, whatsappLink } = getPhoneLinks(c);
                  return (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{c.email || "—"}</td>
                      <td>{c.phone || "—"}</td>
                      <td>
                        {callLink && (
                          <a
                            href={callLink}
                            style={{ marginRight: "12px", color: "#115159" }}
                          >
                            📞 
                          </a>
                        )}
                        {whatsappLink && (
                          <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "green" }}
                          >
                            💬 
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="mobile-card-list">
              {filteredData.map((c) => (
                <article className="mobile-data-card" key={c.id}>
                  <div className="mobile-card-title-row">
                    <h3 className="mobile-card-title">{c.name}</h3>
                  </div>
                  <div className="mobile-card-grid">
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Email</span>
                      <span className="mobile-card-value">{c.email || "—"}</span>
                    </div>
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Phone</span>
                      <span className="mobile-card-value">{c.phone || "—"}</span>
                    </div>
                  </div>
                  {renderContactActions(c)}
                </article>
              ))}
            </div>
          </>
        )}

        {/* 🟣 INACTIVE CUSTOMERS */}
        {type === "inactive-customers" && filteredData.length > 0 && (
          <>
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Last Order</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.map((c) => {
                  const { callLink, whatsappLink } = getPhoneLinks(c);
                  return (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{formatDisplayDate(c.last_order_date, "No Orders")}</td>
                      <td>
                        {callLink && (
                          <a
                            href={callLink}
                            style={{ marginRight: "12px", color: "#115159" }}
                          >
                            📞 
                          </a>
                        )}
                        {whatsappLink && (
                          <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "green" }}
                          >
                            💬 
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="mobile-card-list">
              {filteredData.map((c) => (
                <article className="mobile-data-card" key={c.id}>
                  <div className="mobile-card-title-row">
                    <h3 className="mobile-card-title">{c.name}</h3>
                  </div>
                  <div className="mobile-card-grid">
                    <div className="mobile-card-item">
                      <span className="mobile-card-label">Last Order</span>
                      <span className="mobile-card-value">
                        {formatDisplayDate(c.last_order_date, "No Orders")}
                      </span>
                    </div>
                  </div>
                  {renderContactActions(c)}
                </article>
              ))}
            </div>
          </>
        )}
      </div>

      {!error && rowCount > 0 ? (
        <div className="pagination-bar">
          <div className="pagination-summary">
            Showing page {pagination.page} with {filteredData.length} rows
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={pagination.page <= 1 || isRefreshing}
            >
              Previous
            </button>
            <span className="pagination-page">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              className="pagination-button"
              onClick={() =>
                setPage((current) => Math.min(pagination.totalPages, current + 1))
              }
              disabled={pagination.page >= pagination.totalPages || isRefreshing}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
